import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
} from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";
import App from "./App";
import { getConfig } from "./config";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import history from "./utils/history";

import { getCookieConsentValue } from "react-cookie-consent";
import { CircylCrmContextConfig } from "./hooks/useApi";
import "./tools/i18n";

// google analytics tracking.
const TRACKING_ID = "G-Y2CBXB983J"; // OUR_TRACKING_ID

const cookieValue = getCookieConsentValue("teacheractive-timesheets");
if (cookieValue === "true") {
  ReactGA.initialize(TRACKING_ID);
}

// sentry error capture.
Sentry.init({
  dsn: "https://ddeceb6a9ed26d757b31ea78d4189fdf@o1278925.ingest.us.sentry.io/4507072887848960",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const config = getConfig();

// auth0 configuration
const onRedirectCallback = (appState: AppState | undefined) => {
  history.push(
    appState && appState.returnTo ? appState.returnTo : window.location.pathname
  );
};

const providerConfig: Auth0ProviderOptions = {
  domain: config.domain,
  clientId: config.clientId,
  onRedirectCallback,
  authorizationParams: {
    redirect_uri: window.location.origin,
    ...(config.audience ? { audience: config.audience } : null),
  },
};

//
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: Infinity },
  },
});

const CircylCrmContext = React.createContext<CircylCrmContextConfig>({
  isApiError: false,
  errorMessage: undefined,
  setIsApiError: () => {},
  setErrorMessage: () => {},
  buttonsLocked: false,
  setButtonsLocked: () => {},
});
export const CircylCrmContextProvider = CircylCrmContext.Provider;

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <Auth0Provider {...providerConfig}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
